import { useEffect, useState, useCallback } from 'react';
import request from 'utils/request';
import urls from 'utils/config';
import { getLocalUser } from 'utils/helpers';

const { API_BASE_URL } = urls || {};

export const VERIFICATION_OFFICER_ROLE_NAME = 'Verification Officer';

/**
 * Assigned-to filter options: Me, other Verification Officers, Unassigned.
 * Excludes the current user from the officer list (covered by "Me").
 */
export function buildAssignedToFilterOptions(verificationOfficerOptions = []) {
  let currentUsername = '';
  try {
    const userStr = getLocalUser();
    const user = userStr ? JSON.parse(userStr) : null;
    currentUsername = (
      user?.email ||
      user?.username ||
      user?.userName ||
      user?.primaryEmail ||
      ''
    )
      .toString()
      .trim()
      .toLowerCase();
  } catch {
    currentUsername = '';
  }

  const otherOfficers = (verificationOfficerOptions || []).filter((option) => {
    const value = (option?.value || '').toString().trim().toLowerCase();
    return value && value !== currentUsername;
  });

  return [
    { label: 'Me', value: 'me' },
    ...otherOfficers,
    { label: 'Unassigned', value: 'unassigned' },
  ];
}

/**
 * Loads org users that have the Verification Officer role for assign/filter pickers.
 */
export function useVerificationOfficers(enabled) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const rolesRes = await request({
        method: 'get',
        url: `${API_BASE_URL}/ums/roles`,
      });
      if (!rolesRes.success) {
        setOptions([]);
        return;
      }
      const roles = Array.isArray(rolesRes.raw)
        ? rolesRes.raw
        : rolesRes.raw?.content ||
          rolesRes.raw?.data ||
          rolesRes.raw?.entities ||
          [];
      const voRole = roles.find(
        (r) =>
          r.name === VERIFICATION_OFFICER_ROLE_NAME ||
          r.roleName === VERIFICATION_OFFICER_ROLE_NAME
      );
      if (!voRole?.id) {
        setOptions([]);
        return;
      }
      const usersRes = await request({
        method: 'get',
        url: `${API_BASE_URL}/ums/users?limit=200&offset=0&roleId=${voRole.id}`,
      });
      if (!usersRes.success) {
        setOptions([]);
        return;
      }
      const users = Array.isArray(usersRes.raw)
        ? usersRes.raw
        : usersRes.raw?.data ||
          usersRes.raw?.content ||
          usersRes.raw?.entities ||
          [];
      setOptions(
        users
          .map((u) => {
            const email = u.email || u.username || u.userName;
            const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
            return {
              label: name ? `${name} (${email})` : email,
              value: email,
            };
          })
          .filter((o) => o.value)
      );
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { options, loading, reload: load };
}
